import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';
import { PageOptions, SortConfig } from 'src/services/api.types';
import { SortOrder } from 'src/services/common.types';
import { useGetMyCompaniesLeanQuery } from 'src/services/connections/connections.service';
import { ICompanyListItem } from 'src/services/connections/connections.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { showCompanyEditor } from 'src/store/slices/companies/companiesSlice';
import { useAppDispatch } from 'src/store/store';
import { TenantType } from 'src/types/enums';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import DataListActionsMenu from 'src/components/data-list/data-list-actions-menu';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';

import { buildCompanyColumns } from './company-columns';
import CompanyRowActions from './company-row-actions';

const PAGE_SIZE = 50;

/**
 * Companies list on the shared DataList (JT1-1527/1528, item 27). Configurable/sortable columns
 * (name + logo, city, number of jobs, number of contacts, owner, last updated) from the lean
 * endpoint, per-column search, infinite scroll, and the company actions (view, edit, add task).
 * Replaces the COMPANIES tab's old table. Company editor + task sidesheets are mounted globally.
 */
export default function CompaniesList() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { openRecord } = useRecordPanel();
  const authContext = useAuthContext();
  const tenantType = useOrgTenant();
  const orgId = authContext.organization?.id;

  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ICompanyListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'company_name', sort_order: SortOrder.ASC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  const { currentData, isFetching } = useGetMyCompaniesLeanQuery(
    { params: { search_query: filterQuery || undefined, column_filters: columnSearch, ...pageOptions, ...sortConfig } },
    { skip: !orgId, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  const isSearching = isFetching || searchInput !== filterQuery;

  const resetToFirstPage = useCallback(() => setPage(1), []);

  const applySearch = useMemo(() => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300), [resetToFirstPage]);
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);
  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);
  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => setSelectedIds(checked ? Array.from(new Set(ids)) : []), []);

  const addToList = useBoolean();
  const onSuccessAddToList = useCallback(() => { setSelectedIds([]); addToList.onFalse(); }, [addToList]);

  const handleQuickView = useCallback((id: string) => openRecord('company', id, { mode: 'quick' }), [openRecord]);
  const handleOpenOverlay = useCallback((id: string) => openRecord('company', id, { mode: 'overlay' }), [openRecord]);

  const registry = useMemo(() => buildCompanyColumns({ t, onOpenRecord: handleOpenOverlay }), [t, handleOpenOverlay]);
  const { columns, pickerModel, save } = useColumnPreferences('companies', registry);

  const renderRowLeading = useCallback((row: ICompanyListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: ICompanyListItem) => <CompanyRowActions row={row} onView={handleQuickView} />, [handleQuickView]);

  const canManage = [TenantType.Recruiter, TenantType.Client].includes(tenantType as TenantType);
  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('companies.table.search_placeholder', { defaultValue: 'Search companies' })}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />
        <DataListActionsMenu
          actions={[
            { key: 'add-company', label: t('companies.actions.add_company', { defaultValue: 'Add company' }), icon: 'mingcute:add-line', onClick: () => dispatch(showCompanyEditor(undefined)), hidden: !canManage },
            { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: addToList.onTrue, disabled: !hasSelection },
          ]}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        <DataList<ICompanyListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
          renderRowActions={renderRowActions}
        />
      </Card>

      <RecordPanel type="company" ids={rows.map((r) => r.id)} total={count} />

      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={onSuccessAddToList} items={selectedIds} entityType={ListEntityType.COMPANY} />
    </>
  );
}
